/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {LinkChannelIcon} from '@app/components/icons/LinkChannelIcon';
import {LockedLinkChannelIcon} from '@app/components/icons/LockedLinkChannelIcon';
import {LockedTextChannelIcon} from '@app/components/icons/LockedTextChannelIcon';
import {LockedVoiceChannelIcon} from '@app/components/icons/LockedVoiceChannelIcon';
import {NoConnectChannelIcon} from '@app/components/icons/NoConnectChannelIcon';
import {NSFWLinkChannelIcon} from '@app/components/icons/NSFWLinkChannelIcon';
import {NSFWTextChannelIcon} from '@app/components/icons/NSFWTextChannelIcon';
import {NSFWVoiceChannelIcon} from '@app/components/icons/NSFWVoiceChannelIcon';
import {TextChannelIcon} from '@app/components/icons/TextChannelIcon';
import {VoiceChannelIcon} from '@app/components/icons/VoiceChannelIcon';
import i18n from '@app/I18n';
import type {ChannelRecord} from '@app/records/ChannelRecord';
import ChannelDisplayNameStore from '@app/stores/ChannelDisplayNameStore';
import UserStore from '@app/stores/UserStore';
import {compareChannelPosition} from '@app/utils/ChannelShared';
import * as NicknameUtils from '@app/utils/NicknameUtils';
import {FLUXERBOT_ID} from '@fluxer/constants/src/AppConstants';
import {ChannelTypes, Permissions} from '@fluxer/constants/src/ChannelConstants';
import {msg} from '@lingui/core/macro';
import {CaretDownIcon, type IconProps, NotePencilIcon} from '@phosphor-icons/react';

export function compareChannels(a: ChannelRecord, b: ChannelRecord): number {
	return compareChannelPosition(a, b);
}

function isChannelRoleRequired(channel: {
	type: number;
	guildId?: string;
	permissionOverwrites?: Readonly<Record<string, {deny: bigint}>>;
}): boolean {
	if (!channel.guildId || !channel.permissionOverwrites) return false;
	const overwrite = channel.permissionOverwrites[channel.guildId];
	if (!overwrite) return false;

	switch (channel.type) {
		case ChannelTypes.GUILD_VOICE:
			return (overwrite.deny & Permissions.CONNECT) === Permissions.CONNECT;
		case ChannelTypes.GUILD_TEXT:
		case ChannelTypes.GUILD_LINK:
			return (overwrite.deny & Permissions.VIEW_CHANNEL) === Permissions.VIEW_CHANNEL;
		default:
			return false;
	}
}

export function getIcon(
	channel: {
		type: number;
		nsfw?: boolean;
		guildId?: string;
		permissionOverwrites?: Readonly<Record<string, {deny: bigint}>>;
	},
	props: IconProps = {},
	options?: {locked?: boolean},
) {
	const roleRequired = isChannelRoleRequired(channel);

	switch (channel.type) {
		case ChannelTypes.GUILD_TEXT: {
			if (channel.nsfw) return <NSFWTextChannelIcon {...props} />;
			if (roleRequired) return <LockedTextChannelIcon {...props} />;
			return <TextChannelIcon {...props} />;
		}
		case ChannelTypes.GUILD_VOICE: {
			if (channel.nsfw) return <NSFWVoiceChannelIcon {...props} />;
			if (options?.locked) return <NoConnectChannelIcon {...props} />;
			if (roleRequired) return <LockedVoiceChannelIcon {...props} />;
			return <VoiceChannelIcon {...props} />;
		}
		case ChannelTypes.GUILD_LINK: {
			if (channel.nsfw) return <NSFWLinkChannelIcon {...props} />;
			if (roleRequired) return <LockedLinkChannelIcon {...props} />;
			return <LinkChannelIcon {...props} />;
		}
		case ChannelTypes.GUILD_CATEGORY:
			return <CaretDownIcon weight="bold" {...props} />;
		case ChannelTypes.DM_PERSONAL_NOTES:
			return <NotePencilIcon weight="bold" {...props} />;
		default:
			return <TextChannelIcon {...props} />;
	}
}

export function getName(channel: ChannelRecord) {
	switch (channel.type) {
		case ChannelTypes.GUILD_VOICE:
			return channel.nsfw ? i18n._(msg`Voice (NSFW)`) : i18n._(msg`Voice`);
		case ChannelTypes.GUILD_CATEGORY:
			return i18n._(msg`Category`);
		case ChannelTypes.GUILD_LINK:
			return channel.nsfw ? i18n._(msg`Link (NSFW)`) : i18n._(msg`Link`);
		default:
			return channel.nsfw ? i18n._(msg`Text (NSFW)`) : i18n._(msg`Text`);
	}
}

const getDirectMessageDisplayName = (channel: ChannelRecord): string => {
	if (channel.recipientIds.length === 0) {
		return i18n._(msg`Unknown User`);
	}

	const recipient = UserStore.getUser(channel.recipientIds[0]);
	const nickname = recipient ? NicknameUtils.getNickname(recipient) : null;
	return nickname ?? i18n._(msg`Unknown User`);
};

const getGroupDMDisplayName = (channel: ChannelRecord): string => {
	const customName = channel.name?.trim() ?? '';
	if (customName.length > 0) {
		return customName;
	}

	return ChannelDisplayNameStore.getDisplayName(channel.id) ?? i18n._(msg`Unnamed Group`);
};

export function getDMDisplayName(channel: ChannelRecord): string {
	switch (channel.type) {
		case ChannelTypes.DM_PERSONAL_NOTES:
			return i18n._(msg`Personal Notes`);
		case ChannelTypes.DM:
			return getDirectMessageDisplayName(channel);
		case ChannelTypes.GROUP_DM:
			return getGroupDMDisplayName(channel);
		default:
			return channel.name || i18n._(msg`Unknown Channel`);
	}
}

export function isSystemDmChannel(channel: ChannelRecord): boolean {
	return channel.type === ChannelTypes.DM && channel.recipientIds.includes(FLUXERBOT_ID);
}
